import { requireInspectorIdentifier } from "../dependencies";
import { getDB } from "../util/db";
import { getDateFormatter } from "../util/utils";

const ACTIVE_USER_IDX = 0;
const PREVIOUS_USER_IDX = 1;

export class User {
    constructor() {
		this.token = null;
		this.job = null;
		this.rentalMode = false;
    }

    get fullName() {
        return `${this.firstName} ${this.lastName}`;
    }

	get isCurrentJobShared() {
		if(!!this.job) {
			if(!this.isUidInAccountTree(this.job.userIdentifier)) {
				// Can't start an inspection on a shared job.
				return true;
			}
		}
		return false;
	}

	get isAllowedToStartInspection() {
		if(this.isCurrentJobShared) {
			return false;
		}

		if(!requireInspectorIdentifier) {
			// Nothing required - we're okay
			return true;
		}

		if(!this.inspectorIdentifier) {
			// ID required and not available - fail
			return false;
		}
		if(!this.inspectorExpiration) {
			// No expiration - fail
			return false;
		}
		if(Date.parse(this.inspectorExpiration) < Date.now()) {
			// Expired - fail
			return false;
		}
		// All checks passed
		return true;
	}

	get needsInspectorRecheck() {
		if(!this.inspectorIdentifier) {
			return false; // Can't recheck an inspector that doesn't exist
		}
		if(!this.inspectorExpiration) {
			return true; // Must recheck if there is no expiration
		}

		// Recheck if we are within a month of expiration
		let expDate = Date.parse(this.inspectorExpiration);
		let checkDate = new Date();
		checkDate.setTime(checkDate - 1000 * 60 * 60 * 24 * 30);

		return expDate < checkDate;
	}

	// Takes a UI and tells whether it is in your known account tree (parent account / sub accounts)
	isUidInAccountTree(uid) {
		if(this.ownerId == uid) {
			return true;
		}
		return this.isUidInSelfOrSubaccount(uid);
	}

	isUidInSelfOrSubaccount(uid) {
		if(this.id == uid) {
			return true;
		}
		return this.isUidInSubaccount(uid);
	}

	isUidInSubaccount(uid) {
		let accts = this.subaccounts || [];

		// Server will sometimes return single values as an object, not an array
		if(!Array.isArray(accts)) {
			accts = [accts];
		}

		for(const acct of accts) {
			if(acct.id == uid) {
				return true;
			}
		}
		return false;
	}

	loadInspectorDetails(details) {
		this.inspectorIdentifier = details.mcid;
		this.inspectorName = `${details.firstName} ${details.lastName}`;
		this.inspectorCompany = details.customerName;
		this.inspectorExpiration = details.equipmentInspectorPermExpires;
	}

	get dateFormatter() {
		// NOTE - tried caching - that can prevent saving the profile
		return getDateFormatter(this.prefDateFormat);
	}

	getConnections() {
		if(this.connections == null) {
			return [];
		}
		if(Array.isArray(this.connections)) {
			return this.connections;
		}
		return [this.connections];
	}

	async loadPreviousSessionDataIfRelevant() {
		let pUser = await User.loadPreviousUser();
		if(pUser == null) {
			return; // no previous user
		}
		if(pUser.userName != this.userName) {
			return; // different previous user
		}

		const sessionPreferenceAttributes = ["inspectorIdentifier", "inspectorCompany", "inspectorName", "inspectorExpiration", "rentalMode"];
		for(const att of sessionPreferenceAttributes) {
			this[att] = pUser[att];
		}
	}

    static hydrate(jsonData) {
        if(jsonData == null) { return null }
        let user = Object.assign(new User(), jsonData);
        return user;
    }

	static async saveUser(user) {
		let db = await getDB();
		if(user == null) {
			// Logout
			await db.delete("Users", ACTIVE_USER_IDX);
			// Don't delete previously-logged-in user.
			// We will check to see if they are the same before clearing
			// user data on next login.
		} else {
			// If a different user logged in, clear the data
			let previousUser = await db.get("Users", PREVIOUS_USER_IDX);
			if(previousUser?.userName != user.userName) {
				console.log("New user - clearing previous data");
				await db.clear("Inspections");
				await db.clear("Jobs");
			}

			// Save User
			await db.put("Users", user, ACTIVE_USER_IDX);
			await db.put("Users", user, PREVIOUS_USER_IDX);
		}
	}

	static async loadUserAtIndex(idx) {
		let db = await getDB();
		let theUser = await db.get("Users", idx);
		if(theUser == null) {
			return null;
		} else {
			return User.hydrate(theUser);
		}
	}

	// Loads the current user from the database
	static async loadCurrentUser() {
		return await User.loadUserAtIndex(ACTIVE_USER_IDX);
	}

	static async loadPreviousUser() {
		return await User.loadUserAtIndex(PREVIOUS_USER_IDX);
	}
}